import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShieldCheck, MapPin, Phone, Calendar, MessageSquare, Heart, BedDouble, Bath, Car,
  Maximize, Building2, Compass, Route, Clock, CheckCircle2, AlertTriangle, Play, GitCompare,
} from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { formatPrice, perSqft, typeLabel, assessmentStyle, scoreColor } from "../lib/format";
import { ScoreBar, BigScore } from "../components/Score";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";

const track = (event, property_id, source) => { api.post("/track", { event, property_id, source }).catch(() => {}); };

export default function PropertyDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings, toggleSave, isSaved, toggleCompare } = useApp();
  const [active, setActive] = useState(0);

  const { data: p, isLoading } = useQuery({
    queryKey: ["property", slug],
    queryFn: async () => (await api.get(`/properties/${slug}`)).data,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 py-12"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500">Property not found.</div>;

  const waMsg = encodeURIComponent(`Hi, I am interested in Property ${p.property_id} - ${p.title} in ${p.sector}, Faridabad.`);
  const waLink = `https://wa.me/${settings.whatsapp || ""}?text=${waMsg}`;
  const images = (p.images?.length ? p.images : [""]).map(fileUrl);

  const overview = [
    ["Property Type", typeLabel(p.property_type)], ["Area", p.area_sqft ? `${p.area_sqft} Sq.Ft` : null],
    ["Bedrooms", p.bedrooms], ["Bathrooms", p.bathrooms], ["Floor", p.floor], ["Total Floors", p.total_floors],
    ["Parking", p.parking], ["Road Width", p.road_width], ["Property Age", p.property_age],
    ["Facing", p.facing], ["Furnishing", p.furnishing], ["Transaction", p.transaction_type],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  const subScores = [
    ["Price Value", p.score_price], ["Location", p.score_location], ["Construction", p.score_construction],
    ["Connectivity", p.score_connectivity], ["Investment Potential", p.score_investment],
  ].filter(([, v]) => v);

  const locality = [
    ["Connectivity", p.locality_connectivity], ["Schools", p.locality_schools], ["Hospitals", p.locality_hospitals],
    ["Market", p.locality_market], ["Investment", p.locality_investment],
  ].filter(([, v]) => v);

  return (
    <div className="pb-24 md:pb-8">
      <Seo title={`${p.title}, ${p.sector} — ${formatPrice(p.price)} | Faridabad Property`} description={p.seo_description || `${typeLabel(p.property_type)} for sale in ${p.sector}, Faridabad. ${formatPrice(p.price)}. Personally verified.`} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* breadcrumb */}
        <div className="text-sm text-slate-500 mb-4"><Link to="/properties" className="hover:text-indigo-800">Properties</Link> / <span className="text-slate-700">{p.property_id}</span></div>

        {/* Gallery */}
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-3">
          <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]">
            <img src={images[active]} alt={p.title} className="w-full h-full object-cover" data-testid="gallery-main" />
            {p.personally_verified && <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full"><ShieldCheck className="w-4 h-4" /> Personally Verified</span>}
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-2 gap-3">
            {images.slice(0, 4).map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`rounded-xl overflow-hidden aspect-[4/3] border-2 ${active === i ? "border-indigo-700" : "border-transparent"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            {p.video_url && (
              <a href={fileUrl(p.video_url)} target="_blank" rel="noreferrer" className="rounded-xl bg-slate-900 text-white flex flex-col items-center justify-center aspect-[4/3] gap-1" data-testid="watch-video">
                <Play className="w-6 h-6" /><span className="text-xs">Watch Video</span>
              </a>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-8">
          <div className="space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 text-slate-500 text-sm"><MapPin className="w-4 h-4 text-indigo-700" /> {p.sector}, Faridabad · {p.property_id}</div>
              <h1 className="font-heading text-2xl sm:text-4xl font-extrabold text-slate-900 mt-1">{p.title}</h1>
              <div className="flex flex-wrap items-baseline gap-3 mt-3">
                <span className="font-heading text-3xl font-extrabold text-indigo-900">{formatPrice(p.price)}</span>
                {perSqft(p.price, p.area_sqft) && <span className="text-slate-500">{perSqft(p.price, p.area_sqft)}</span>}
                {p.negotiable && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Negotiable</span>}
              </div>
              {p.last_verified_date && <div className="text-xs text-slate-400 mt-1">Last Verified: {p.last_verified_date}</div>}
            </div>

            {/* Overview */}
            <section className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Property Overview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {overview.map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2">
                    <div className="text-slate-400 mt-0.5"><Building2 className="w-4 h-4" /></div>
                    <div><div className="text-xs text-slate-400">{k}</div><div className="text-sm font-semibold text-slate-800">{v}</div></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Property Score */}
            {p.score_overall ? (
              <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="property-score-section">
                <BigScore value={p.score_overall} />
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-6">
                  {subScores.map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}
                </div>
              </section>
            ) : null}

            {/* Price Analysis */}
            {(p.fair_price_min || p.price_assessment) && (
              <section className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl border border-indigo-100 p-6" data-testid="price-analysis-section">
                <h2 className="font-heading text-xl font-bold text-slate-900">Is {formatPrice(p.price)} a Fair Price?</h2>
                <div className="grid sm:grid-cols-3 gap-4 mt-5">
                  <div className="bg-white rounded-xl p-4 border border-slate-100"><div className="text-xs text-slate-400">Seller Asking Price</div><div className="font-heading font-bold text-lg text-slate-900 mt-1">{formatPrice(p.asking_price || p.price)}</div></div>
                  {p.fair_price_min && <div className="bg-white rounded-xl p-4 border border-slate-100"><div className="text-xs text-slate-400">Estimated Fair Price</div><div className="font-heading font-bold text-lg text-indigo-800 mt-1">{formatPrice(p.fair_price_min)} – {formatPrice(p.fair_price_max)}</div></div>}
                  {p.price_assessment && <div className="bg-white rounded-xl p-4 border border-slate-100"><div className="text-xs text-slate-400">Our Verdict</div><div className={`inline-block mt-2 text-sm font-semibold px-2.5 py-1 rounded-full border ${assessmentStyle(p.price_assessment)}`}>{p.price_assessment}</div></div>}
                </div>
                <p className="text-xs text-slate-400 mt-4">Price assessment is an informational opinion based on available local/property data and is not a formal valuation.</p>
              </section>
            )}

            {/* Why recommend / things to know */}
            {(p.why_recommend?.length || p.things_to_know?.length) ? (
              <section className="grid sm:grid-cols-2 gap-4">
                {p.why_recommend?.length ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <h3 className="font-heading font-bold text-slate-900 mb-3">Why We Recommend This</h3>
                    <ul className="space-y-2">{p.why_recommend.map((w, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" /> {w}</li>)}</ul>
                  </div>
                ) : null}
                {p.things_to_know?.length ? (
                  <div className="bg-white rounded-2xl border border-amber-200 p-6">
                    <h3 className="font-heading font-bold text-slate-900 mb-3">Things You Should Know</h3>
                    <ul className="space-y-2">{p.things_to_know.map((w, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> {w}</li>)}</ul>
                  </div>
                ) : null}
              </section>
            ) : null}

            {/* Locality Intelligence */}
            {locality.length ? (
              <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="locality-section">
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Locality Intelligence — {p.sector}</h2>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">{locality.map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}</div>
                {p.nearby && Object.keys(p.nearby).length ? (
                  <div className="grid sm:grid-cols-2 gap-4 mt-6">
                    {Object.entries(p.nearby).map(([cat, items]) => (
                      <div key={cat}><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{cat}</div>
                        <div className="text-sm text-slate-700">{(items || []).join(", ")}</div></div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>

          {/* Sticky sidebar CTA (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="font-heading text-2xl font-extrabold text-indigo-900">{formatPrice(p.price)}</div>
              <div className="text-sm text-slate-500">{perSqft(p.price, p.area_sqft)}</div>
              <div className="space-y-2 mt-5">
                <Button onClick={() => nav(`/book-site-visit?property=${p.id}`)} data-testid="detail-book-visit" className="w-full bg-indigo-900 hover:bg-indigo-800"><Calendar className="w-4 h-4 mr-2" /> Book Site Visit</Button>
                <a href={waLink} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", p.id, "detail")}><Button variant="outline" data-testid="detail-whatsapp" className="w-full border-indigo-600 text-indigo-700 hover:bg-indigo-50"><MessageSquare className="w-4 h-4 mr-2" /> WhatsApp Enquiry</Button></a>
                <a href={`tel:${settings.phone || ""}`} onClick={() => track("call_click", p.id, "detail")}><Button variant="outline" data-testid="detail-call" className="w-full"><Phone className="w-4 h-4 mr-2" /> Talk to Expert</Button></a>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button variant="ghost" onClick={() => toggleSave(p.id)} className={isSaved(p.id) ? "text-rose-600" : "text-slate-600"}><Heart className="w-4 h-4 mr-1" fill={isSaved(p.id) ? "currentColor" : "none"} /> Save</Button>
                  <Button variant="ghost" onClick={() => toggleCompare(p.id)} className="text-slate-600"><GitCompare className="w-4 h-4 mr-1" /> Compare</Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 px-3 py-2.5 grid grid-cols-4 gap-2" data-testid="mobile-cta-bar">
        <button onClick={() => toggleSave(p.id)} data-testid="mcta-save" className={`flex flex-col items-center gap-0.5 text-[11px] ${isSaved(p.id) ? "text-rose-600" : "text-slate-600"}`}><Heart className="w-5 h-5" fill={isSaved(p.id) ? "currentColor" : "none"} /> Save</button>
        <a href={waLink} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", p.id, "mobile")} data-testid="mcta-whatsapp" className="flex flex-col items-center gap-0.5 text-[11px] text-indigo-700"><MessageSquare className="w-5 h-5" /> WhatsApp</a>
        <a href={`tel:${settings.phone || ""}`} onClick={() => track("call_click", p.id, "mobile")} data-testid="mcta-call" className="flex flex-col items-center gap-0.5 text-[11px] text-slate-600"><Phone className="w-5 h-5" /> Call</a>
        <button onClick={() => nav(`/book-site-visit?property=${p.id}`)} data-testid="mcta-visit" className="flex flex-col items-center gap-0.5 text-[11px] text-white bg-indigo-900 rounded-lg py-1"><Calendar className="w-5 h-5" /> Visit</button>
      </div>
    </div>
  );
}
